/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";

import EmptyTemplate from "@src/components/modules/TemplateModule/EmptyTemplate";
// import Logo from "@src/components/ui/Logo"; -- the lockup is AuthLayout's now
import LoginForm from "@src/components/modules/LoginModule/LoginForm";
import AuthLayout, { CardHead } from "@src/components/modules/LoginModule/AuthLayout";

import userStore from "@src/stores/UserStore";
import configStore from "@src/utils/Config";
import disclaimerStore from "@src/stores/DisclaimerStore";

import { ThemePalette, ThemeProps } from "@src/components/Theme";
import { ssoOptions } from "@src/sources/SsoSource";
import { forgetLocalData } from "@src/utils/SessionGuard";

/*
 * Swift sign-in, in signin.tech9iq.com's look (AuthLayout, Customer-Login's split layout): the dark
 * Tech9IQ brand half beside the form half, one white card, and the legal footer, which also carries
 * the open-source licence notice (AGPL: keep it). Sign in with Tech9IQ is the primary action; Swift's
 * own form (only at /login?local=1) comes second.
 */
/*
 * Replaced by AuthLayout (pale page, one 420px card, Tech9IQ | Swift lockup); kept for reference:
// (was) Swift sign-in, in the Tech9IQ family look: pale workspace, one white card with the Tech9IQ | Swift
// lockup, the form, and a quiet footer carrying the company and the open-source legal notice.
//
const Wrapper = styled.div<any>`
  position: absolute;
  inset: 0;
  overflow: auto;
  background: ${ThemePalette.grayscale[7]};
`;
const Content = styled.div<any>`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px 24px 16px;
  box-sizing: border-box;
`;
const Card = styled.div<any>`
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
  padding: 36px 36px 32px 36px;
  background: var(--t9-bg-surface);
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.08);
`;
const Title = styled.h1<any>`
  margin: 28px 0 6px 0;
  font-size: 22px;
  line-height: 30px;
  font-weight: 600;
  color: ${ThemePalette.black};
`;
const Lead = styled.p<any>`
  margin: 0 0 24px 0;
  font-size: 14px;
  line-height: 21px;
  color: ${ThemePalette.grayscale[5]};
`;
const Top = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const StyledLoginForm = styled(LoginForm)``;
const Footer = styled.div<any>`
  margin-top: 28px;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  color: ${ThemePalette.grayscale[5]};
  max-width: 520px;
`;
*/
const StyledLoginForm = styled(LoginForm)``;
const Disclaimer = styled.div<any>`
  background: var(--t9-bg-surface);
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: ${ThemeProps.borderRadius};
  position: relative;
  padding: 8px;
  margin-top: 16px;
  overflow-wrap: break-word;
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-wrap;
  color: ${ThemePalette.grayscale[4]};
  width: fit-content;
  height: fit-content;
  /* max-width: 50%; max-height: 25%; -- now under the 448px card, not the whole page */
  max-width: 100%;
  max-height: 25vh;
  box-sizing: border-box;
`;

// Why the person is here (2026-11-03): their session ended, or they signed out (server/sso/routes.ts /login).
const Notice = styled.p`
  margin: 0 0 20px;
  padding: 12px 16px;
  border: 1px solid var(--t9-info-200);
  border-radius: 8px;
  background: var(--t9-info-50);
  color: var(--t9-info-700);
  font-size: 14px;
  line-height: 20px;
`;
const NOTICES: Record<string, string> = {
  ended: "Your SwiftIQ session has ended. Sign in again to continue.",
  expired: "Your SwiftIQ session has ended. Sign in again to continue.",
  signedout: "You have signed out of SwiftIQ.",
};
const reasonNotice = (): string | null =>
  NOTICES[new URLSearchParams(window.location.search).get("reason") || ""] || null;
/** The page to go back to after signing in: only a path on this site. */
const prevPath = (): string | null => {
  const prev = new URLSearchParams(window.location.search).get("prev");
  return prev && /^\/(?!\/)/.test(prev) && !prev.includes("\\") && !/^\/login(?:[/?#]|$)/.test(prev) ? prev : null;
};

type Props = {
  onNavigate: (path: string) => void;
};

type State = {
  domain: string;
};

@observer
class LoginPage extends React.Component<Props, State> {
  state = {
    domain: "",
  };

  UNSAFE_componentWillMount() {
    this.setState({
      domain: userStore.domainName,
    });
  }

  componentDidMount() {
    // A session that ended leaves nothing of its answers in this browser.
    if (reasonNotice()) forgetLocalData();
    document.title = "Sign in · SwiftIQ";

    disclaimerStore.loadDisclaimer();
  }

  renderDisclaimer() {
    if (!disclaimerStore.disclaimer) {
      return null;
    }
    return <Disclaimer>{disclaimerStore.disclaimer}</Disclaimer>;
  }

  async handleFormSubmit(data: { username: string; password: string }) {
    await userStore.login({
      name: data.username,
      password: data.password,
      domain: this.state.domain,
    });
    if (!userStore.loggedIn) {
      return;
    }
    // const prevExp = /\?prev=(.*)/; -- took ?prev= undecoded; the server's page gate sends it encoded
    // const prevMatch = prevExp.exec(window.location.search);
    const prev = prevPath();
    if (prev) {
      this.props.onNavigate(prev);
    } else {
      this.props.onNavigate("/");
    }
  }

  render() {
    const sso = ssoOptions();
    return (
      <EmptyTemplate>
        <AuthLayout
          after={this.renderDisclaimer()}
          notice="SwiftIQ by Tech9IQ includes open-source software licensed under the GNU AGPL v3."
        >
          <CardHead
            title="Sign in to SwiftIQ"
            sub={
              sso.enabled
                ? "Sign in with your Tech9IQ account to move and protect workloads across every cloud you run."
                : "Move and protect workloads across every cloud you run."
            }
          />
          {reasonNotice() ? <Notice role="status">{reasonNotice()}</Notice> : null}
          <StyledLoginForm
            domain={this.state.domain}
            onDomainChange={domain => {
              this.setState({ domain });
            }}
            onFormSubmit={data => this.handleFormSubmit(data)}
            loading={userStore.loading}
            loginFailedResponse={userStore.loginFailedResponse}
            showUserDomainInput={configStore.config.showUserDomainInput}
          />
        </AuthLayout>
      </EmptyTemplate>
    );
  }
}

/* The single-card render AuthLayout replaced, kept for reference:
  render() {
    return (
      <EmptyTemplate>
        <Wrapper>
          <Content>
            <Top>
              <Card>
                <Logo large to="/login" />
                <Title>Sign in to SwiftIQ</Title>
                <Lead>Move and protect workloads across every cloud you run.</Lead>
                <StyledLoginForm
                  domain={this.state.domain}
                  onDomainChange={domain => {
                    this.setState({ domain });
                  }}
                  onFormSubmit={data => this.handleFormSubmit(data)}
                  loading={userStore.loading}
                  loginFailedResponse={userStore.loginFailedResponse}
                  showUserDomainInput={configStore.config.showUserDomainInput}
                />
              </Card>
              {this.renderDisclaimer()}
            </Top>
            <Footer>
              SwiftIQ by Tech9IQ · © 2026 Integrated Tech9labs Pvt. Ltd.
              <br />
              Includes open-source software licensed under the GNU AGPL v3.
            </Footer>
          </Content>
        </Wrapper>
      </EmptyTemplate>
    );
  }
*/

function LoginPageWithNavigate() {
  const navigate = useNavigate();

  return <LoginPage onNavigate={navigate} />;
}

export default LoginPageWithNavigate;
